"use client";

import {
  runArtmatchEmbedInBackground,
  shouldTriggerArtmatchEmbed,
  useEmbedAttachment,
} from "@/api/artmatch";
import {
  Attachment,
  useDuplicateAttachmentToDocument,
} from "@/api/attachment";
import StyledDrawer from "@/components/StyledDrawer";
import { Button } from "@/components/ui/button";
import { ConfirmActionDialog } from "@/components/ui/confirm-action-dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import {
  isMainPosition,
  optionsPosition,
  TypeOptionPosition,
} from "@/lib/image";
import { isAxiosError } from "axios";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { UniqueImageFullScreen } from "../modal/UniqueImageFullScreen";

type DuplicateFromMasterDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceAttachment: Attachment | null;
  targetDocId: string;
  childType: TypeOptionPosition;
  initialPosition: string;
  childAttachments: Attachment[];
  computeImageName?: (position: string) => string;
  onSuccess?: () => void;
  embedAttachmentOnSubmit?: boolean;
  disabled?: boolean;
  stacked?: boolean;
};

export const DuplicateFromMasterDrawer = ({
  open,
  onOpenChange,
  sourceAttachment,
  targetDocId,
  childType,
  initialPosition,
  childAttachments,
  computeImageName,
  onSuccess,
  embedAttachmentOnSubmit = false,
  disabled = false,
  stacked = false,
}: DuplicateFromMasterDrawerProps) => {
  const { t } = useTranslation();
  const vt = useVisibleT();
  const [position, setPosition] = useState(initialPosition);
  const [published, setPublished] = useState(false);
  const [openFacePositionWarning, setOpenFacePositionWarning] = useState(false);
  const { mutateAsync: duplicateAttachment, isPending } =
    useDuplicateAttachmentToDocument();
  const { mutateAsync: embedAttachment } = useEmbedAttachment();

  useEffect(() => {
    if (open) {
      setPosition(initialPosition);
      setPublished(false);
    }
  }, [open, initialPosition]);

  const isPositionAlreadyAssigned = (pos: string) =>
    isMainPosition(pos) &&
    childAttachments.some((a) => a.position === pos);

  const previewUrl =
    sourceAttachment?.mediumPublicUrl ??
    sourceAttachment?.publicUrl ??
    sourceAttachment?.largePublicUrl;

  const handleConfirm = async () => {
    if (!sourceAttachment || disabled) return;

    if (isPositionAlreadyAssigned(position)) {
      setOpenFacePositionWarning(true);
      return;
    }

    try {
      const created = await duplicateAttachment({
        sourceAttachmentId: sourceAttachment.id,
        targetDocId,
        position,
        published,
        name: computeImageName?.(position),
      });
      if (
        embedAttachmentOnSubmit &&
        created.id &&
        shouldTriggerArtmatchEmbed(
          { position: "", published: false },
          { position, published, images: [] },
        )
      ) {
        runArtmatchEmbedInBackground(embedAttachment, created.id);
      }
      toast({
        variant: "success",
        description: t("mediaLibrary.duplicateFromMaster.success"),
      });
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      if (isAxiosError(e)) {
        toast({
          variant: "error",
          description: e.response?.data?.message ?? t("unknownError"),
        });
        return;
      }
      toast({
        variant: "error",
        description: t("unknownError"),
      });
    }
  };

  return (
    <>
      <StyledDrawer
        title={t("mediaLibrary.duplicateFromMaster.title")}
        className={stacked ? "min-w-[500px] z-[60]" : "min-w-[500px]"}
        maxWidth="sm:max-w-lg"
        sheetProps={{ open, onOpenChange }}
        footer={
          <div className="flex w-full justify-end gap-2 p-4">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              {t("formCancel")}
            </Button>
            <Button onClick={handleConfirm} disabled={disabled || isPending}>
              {t("mediaLibrary.duplicateFromMaster.confirm")}
            </Button>
          </div>
        }
      >
        {previewUrl && sourceAttachment && (
          <div className="mb-4 flex justify-center">
            <UniqueImageFullScreen
              media={{
                url: previewUrl,
                largeUrl: sourceAttachment.largePublicUrl ?? previewUrl,
                alt: "",
                className: "max-h-[240px] w-full object-contain",
              }}
            />
          </div>
        )}
        <div className="space-y-4">
          <div>
            <Label className="mb-1.5 block">
              {t("attachment.form.position")}
            </Label>
            <Select
              value={position}
              onValueChange={(value) => {
                if (isPositionAlreadyAssigned(value)) {
                  setOpenFacePositionWarning(true);
                  return;
                }
                setPosition(value);
                if (isMainPosition(value)) {
                  setPublished(true);
                }
              }}
              disabled={disabled || isPending}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {optionsPosition[childType].map((pos) => (
                  <SelectItem key={pos} value={pos}>
                    {t(pos)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1.5 block">
              {t("imageFieldPublishedLabel")}
            </Label>
            <Select
              value={published ? "true" : "false"}
              onValueChange={(v) => setPublished(v === "true")}
              disabled={disabled || isPending}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">{t("imageFieldPublished")}</SelectItem>
                <SelectItem value="false">
                  {t("imageFieldNotPublished")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </StyledDrawer>
      <ConfirmActionDialog
        open={openFacePositionWarning}
        onOpenChange={setOpenFacePositionWarning}
        message={vt(
          "positionFaceUniqueWarning",
          'La position "Face" détermine l\'image principale.',
        )}
        confirmLabel={t("formOk")}
        onConfirm={() => {}}
        contentClassName="p-6 max-w-md"
      />
    </>
  );
};
